import { ComponentConfig, ValidationRule } from "../../services/types/ComponentTypes";

/**
 * Resolve a single [ref] (e.g. [qty], [issued_qty]) to a value from row data.
 * Ref is matched against control's Json_Attribute, name, or BindValue.
 */
function resolveRefInRow(
  ref: string,
  row: Record<string, any>,
  childControls: ComponentConfig[] | undefined
): string | null {
  if (!childControls?.length) return null;
  const cleanRef = ref.replace(/[\[\]]/g, "").trim();
  for (const c of childControls) {
    const anyC = c as any;
    const jsonAttr = anyC.Json_Attribute;
    const name = anyC.name ?? anyC.BindValue;
    if (jsonAttr === cleanRef || name === cleanRef) {
      const val = row[name] ?? row[jsonAttr] ?? row[cleanRef];
      if (val !== undefined && val !== null) return String(val);
      return null;
    }
  }
  // Fallback: use ref as key
  const val = row[cleanRef];
  if (val !== undefined && val !== null) return String(val);
  return null;
}

/**
 * Evaluate a condition string against row data.
 * Condition uses [field] refs that map to Json_Attribute or name (e.g. [qty] >= [issued_qty]).
 * Returns true if the condition holds, false otherwise.
 * Safe: only replaces refs with values and evaluates comparison expression.
 */
export function evaluateCondition(
  condition: string,
  row: Record<string, any>,
  childControls: ComponentConfig[] | undefined
): boolean {
  if (!condition || typeof condition !== "string") return true;
  const refRegex = /\[[\w]+\]/g;
  let expr = condition;
  const matches = [...new Set(condition.match(refRegex) || [])];
  for (const m of matches) {
    const resolved = resolveRefInRow(m, row, childControls);
    const num = resolved !== null && resolved !== "" ? parseFloat(resolved) : NaN;
    const replacement = Number.isFinite(num) ? String(num) : (resolved !== null ? `"${String(resolved).replace(/"/g, '\\"')}"` : "0");
    expr = expr.split(m).join(replacement);
  }
  // Normalize operators (allow spaces)
  expr = expr.replace(/\s+/g, " ").trim();
  // Only allow safe characters: numbers, quotes, ., <=, >=, <, >, ==, !=, +, -, *, /, (, ), and spaces
  if (!/^[\d.\s+\-*/()<=>!="]+$/.test(expr)) return true;
  try {
    const result = new Function(`return (${expr})`)();
    return Boolean(result);
  } catch {
    return true;
  }
}

export interface ConditionalValidationResult {
  message: string;
  isWarning: boolean;
  rowIndex: number;
  fieldLabel?: string;
}

/**
 * Run all conditional validations (ValidationConfig with rule CONDITION or condition prop)
 * on each row of submodule data. Returns list of failed validations (condition evaluated to false).
 */
export function runConditionalValidations(
  data: Record<string, any>[],
  childControls: ComponentConfig[] | undefined
): ConditionalValidationResult[] {
  const results: ConditionalValidationResult[] = [];
  if (!data?.length || !childControls?.length) return results;

  for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
    const row = data[rowIndex];
    for (const control of childControls) {
      const anyC = control as any;
      const validation = anyC.validation;
      const conditionConfig = anyC.conditionalValidation;
      const label = anyC.label ?? anyC.Label ?? anyC.name ?? anyC.BindValue ?? "Field";

      if (conditionConfig?.condition) {
        const ok = evaluateCondition(conditionConfig.condition, row, childControls);
        if (!ok && conditionConfig.message) {
          results.push({
            message: conditionConfig.message,
            isWarning: Boolean(conditionConfig.isWarning),
            rowIndex: rowIndex + 1,
            fieldLabel: label,
          });
        }
      }
      if (validation && Array.isArray(validation)) {
        for (const rule of validation) {
          if (rule.rule === ValidationRule.CONDITION && rule.condition) {
            const ok = evaluateCondition(rule.condition, row, childControls);
            if (!ok && rule.message) {
              results.push({
                message: rule.message,
                isWarning: Boolean(rule.isWarning),
                rowIndex: rowIndex + 1,
                fieldLabel: label,
              });
            }
          }
        }
      }
    }
  }
  return results;
}
